import { ChainId, Currency, CurrencyAmount, Percent } from "@taraswap/sdk-core";
import { Field } from "components/swap/constants";
import { parsedQueryString } from "hooks/useParsedQueryString";
import { ParsedQs } from "qs";
import { Dispatch, ReactNode, SetStateAction, createContext } from "react";
import {
  InterfaceTrade,
  RouterPreference,
  TradeState,
} from "state/routing/types";
import { SwapTab } from "uniswap/src/types/screens/interface";

export type SwapInfo = {
  currencies: { [field in Field]?: Currency };
  currencyBalances: { [field in Field]?: CurrencyAmount<Currency> };
  inputTax: Percent;
  outputTax: Percent;
  outputFeeFiatValue?: number;
  parsedAmount?: CurrencyAmount<Currency>;
  inputError?: ReactNode;
  trade: {
    trade?: InterfaceTrade;
    state: TradeState;
    uniswapXGasUseEstimateUSD?: number;
    error?: any;
    swapQuoteLatency?: number;
  };
  allowedSlippage: Percent;
  autoSlippage: Percent;
};

type SwapContextType = {
  swapState: SwapState;
  derivedSwapInfo: SwapInfo;
  setSwapState: Dispatch<SetStateAction<SwapState>>;
};

function parseTokenAmountURLParameter(urlParam: any): string {
  return typeof urlParam === "string" && !isNaN(parseFloat(urlParam))
    ? urlParam
    : "";
}

function parseIndependentFieldURLParameter(urlParam: any): Field {
  return typeof urlParam === "string" && urlParam.toLowerCase() === "output"
    ? Field.OUTPUT
    : Field.INPUT;
}

export function queryParametersToSwapState(parsedQs: ParsedQs): SwapState {
  const typedValue = parseTokenAmountURLParameter(parsedQs.exactAmount);
  const independentField = parseIndependentFieldURLParameter(
    parsedQs.exactField
  );

  return {
    typedValue,
    independentField,
  };
}

export const EMPTY_DERIVED_SWAP_INFO: SwapInfo = Object.freeze({
  currencies: {},
  currencyBalances: {},
  inputTax: new Percent(0),
  outputTax: new Percent(0),
  autoSlippage: new Percent(0),
  allowedSlippage: new Percent(0),
  trade: {
    state: TradeState.LOADING,
  },
});

export const initialSwapState: SwapState = queryParametersToSwapState(
  parsedQueryString()
);

export const SwapContext = createContext<SwapContextType>({
  swapState: initialSwapState,
  derivedSwapInfo: EMPTY_DERIVED_SWAP_INFO,
  setSwapState: () => undefined,
});

type SwapAndLimitContextType = {
  currencyState: CurrencyState;
  prefilledState: {
    inputCurrency?: Currency;
    outputCurrency?: Currency;
  };
  setSelectedChainId: Dispatch<SetStateAction<ChainId | undefined>>;
  setCurrencyState: Dispatch<SetStateAction<CurrencyState>>;
  currentTab: SwapTab;
  setCurrentTab: Dispatch<SetStateAction<SwapTab>>;
  // The chainId of the page/context - can be different from the connected Chain ID if the
  // page is displaying content for a different chain or if multichain UX is enabled
  chainId?: ChainId;
  multichainUXEnabled?: boolean;
};

export const SwapAndLimitContext = createContext<SwapAndLimitContextType>({
  currencyState: {
    inputCurrency: undefined,
    outputCurrency: undefined,
  },
  setCurrencyState: () => undefined,
  setSelectedChainId: () => undefined,
  prefilledState: {
    inputCurrency: undefined,
    outputCurrency: undefined,
  },
  chainId: ChainId.TARAXA,
  currentTab: SwapTab.Swap,
  setCurrentTab: () => undefined,
  multichainUXEnabled: false,
});

export interface SerializedCurrencyState {
  inputCurrencyId?: string;
  outputCurrencyId?: string;
  chainId?: number;
}

// shared state between Swap and Limit
export interface CurrencyState {
  inputCurrency?: Currency;
  outputCurrency?: Currency;
}

export interface SwapState {
  readonly independentField: Field;
  readonly typedValue: string;
  routerPreferenceOverride?: RouterPreference.API;
}
